import { defineStore } from "pinia";
import { nextTick } from "vue";
import czpremadeblocks from "@/store/premadeblocks/czpremadeblocks";
import premadeblocks from "@/store/premadeblocks/premadeblocks";
import { migrateApiBlock } from "@/utils/migrateApiBlocks";
import { resolvePremadeBlocks } from "@/utils/resolvePremadeBlocks";
import shortid from "shortid";
import {
  IOptionsPremadeBlock,
  PremadeBlocksSchema,
  LegacyPremadeBlocksSchema,
  PremadeBlocksOverrideSchema,
  PRESET_GROUPS,
  PRESET_IDS,
  ILegacyPremadeBlocks,
  IPremadeBlocks,
} from "@/schemas/premadeBlock/premadeBlockSchema";
import { SelectPrimaryBlockLanguage } from "@/schemas/template/languageSchema";
import { useMjmlStore } from "./mjmlStore";
import { useNotificationsStore } from "@/stores/notifications";
import { useGeneralStore } from "@/stores/generalStore";
import { i18n } from "@/init/i18n-setup";

interface State {
  premadeBlocks: IPremadeBlocks | ILegacyPremadeBlocks | null | false;
}

interface addPremadeBlock {
  sectionIndex: number;
  blockIndex: number;
  blockType: number;
  emptyBlock?: boolean;
  byClick?: boolean;
}

export const usePremadeBlocksStore = defineStore("premadeBlocks", {
  state: (): State => ({
    premadeBlocks: null,
  }),

  actions: {
    async addPremadeBlock(data: addPremadeBlock) {
      const mjmlStore = useMjmlStore();
      mjmlStore.takeSnapshot();

      // When premade blocks are disabled,
      // we cannot add them since there is nothing to add
      if (this.premadeBlocks === false) {
        return;
      }

      const premBlock: IPremadeBlocks[number]["blocks"][number] = JSON.parse(
        JSON.stringify(
          (this.premadeBlocks as IPremadeBlocks)?.[data.blockType].blocks[
            data.blockIndex
          ]
        )
      );

      if (!premBlock || !premBlock.definition) {
        return;
      }
      const props = premBlock.definition;

      let newIndex = data.sectionIndex;
      if (
        !data.byClick &&
        newIndex !== 0 &&
        newIndex !== mjmlStore.mjml.children[0].children.length
      ) {
        const { newIndex: correntNew } = mjmlStore.getCorrectSectionIndexes({
          oldIndex: 0,
          newIndex: data.sectionIndex,
        });
        newIndex = correntNew;
      }
      const variantOffset = mjmlStore.getSectionIndexVariantOffset(newIndex);

      let sectionIndex = newIndex + variantOffset;

      let primaryTranslation: any = {};
      let primaryLanguage: string | undefined;
      if (mjmlStore.mjml.langs && mjmlStore.mjml.langs.length > 0) {
        primaryLanguage = mjmlStore.mjml.langs.find(
          (lang) => lang.primary
        )?.key;
      }

      if (
        props &&
        premBlock.translation &&
        Object.keys(premBlock.translation).length > 0 &&
        (!mjmlStore.mjml.langs ||
          (mjmlStore.mjml.langs &&
            primaryLanguage &&
            !premBlock.translation[primaryLanguage]))
      ) {
        const langs = Object.keys(premBlock.translation);
        const title = !mjmlStore.mjml.langs
          ? i18n.global.t("layout.blockLanguageSelector.selectMutation")
          : i18n.global.t("layout.blockLanguageSelector.selectPrimaryLanguage");
        const description = !mjmlStore.mjml.langs
          ? i18n.global.t(
              "layout.blockLanguageSelector.selectMutationDescription"
            )
          : i18n.global.t(
              "layout.blockLanguageSelector.selectPrimaryLanguageDescription"
            );
        if (langs.length > 1) {
          const selectedLang = await selectPrimaryLanguage({
            langs: langs,
            title: title,
            description: description,
          });
          if (!selectedLang) return;
          primaryTranslation = premBlock.translation[selectedLang];
        } else {
          primaryTranslation = premBlock.translation[langs[0]];
        }
      }

      let firstFeedBlock: {
        sectionIndex: number;
        columnIndex: number;
        blockIndex: number;
        uid: string;
      } | null = null;

      if (Array.isArray(props)) {
        for (const section of props) {
          for (const property in mjmlStore.defaultSectionProps) {
            // eslint-disable-next-line no-prototype-builtins
            if (mjmlStore.defaultSectionProps.hasOwnProperty(property)) {
              // @ts-expect-error TODO: fix noImplicitAny error here
              section[property] =
                mjmlStore.defaultSectionProps[
                  property as keyof typeof mjmlStore.defaultSectionProps
                ];
            }
          }
          for (const [colIdx, col] of section.children.entries()) {
            col["uid"] = shortid.generate();
            col.children = col.children.map((b: any) => migrateApiBlock(b));
            for (const [blockIdx, block] of col.children.entries()) {
              const previousUid = block.uid;
              block["uid"] = shortid.generate();
              if (block.tagName === "mj-feed-block" && !firstFeedBlock) {
                firstFeedBlock = {
                  sectionIndex,
                  columnIndex: colIdx,
                  blockIndex: blockIdx,
                  uid: block.uid,
                };
              }
              const blockTranslation = primaryTranslation[previousUid];
              if (!mjmlStore.mjml.langs && blockTranslation) {
                if ("content" in blockTranslation && "content" in block) {
                  block.content = blockTranslation.content;
                }
                if ("attributes" in blockTranslation) {
                  for (const attribute in blockTranslation.attributes) {
                    block.attributes[attribute] =
                      blockTranslation.attributes[attribute];
                  }
                }
                if ("children" in blockTranslation && "children" in block) {
                  for (let i = 0; i < block.children.length; i++) {
                    const child = block.children[i];
                    const childTranslation = blockTranslation.children?.[i];
                    if (
                      childTranslation &&
                      "attributes" in childTranslation &&
                      child.attributes
                    ) {
                      for (const attribute in childTranslation.attributes) {
                        // @ts-expect-error TODO: fix noImplicitAny error here
                        child.attributes[attribute] =
                          childTranslation.attributes[attribute];
                      }
                    }
                  }
                }
              }
              if (mjmlStore.mjml.langs) {
                mjmlStore.addSpecialBlockTranslations({
                  block: block,
                  previousUid: previousUid,
                  translations: premBlock.translation,
                  primaryTranslation: primaryTranslation,
                });
              }
            }
          }

          section["uid"] = shortid.generate();
          // @ts-expect-error TODO: fix noImplicitAny error here
          delete section.sectionIndex;

          mjmlStore.mjml.children[0].children.splice(sectionIndex, 0, section);
          sectionIndex++;
        }
      } else {
        for (const property in mjmlStore.defaultSectionProps) {
          // eslint-disable-next-line no-prototype-builtins
          if (mjmlStore.defaultSectionProps.hasOwnProperty(property)) {
            // @ts-expect-error TODO: fix noImplicitAny error here
            props[property] =
              mjmlStore.defaultSectionProps[
                property as keyof typeof mjmlStore.defaultSectionProps
              ];
          }
        }
        for (const [colIdx, col] of props.children.entries()) {
          col["uid"] = shortid.generate();
          col.children = col.children.map((b: any) => migrateApiBlock(b));
          for (const [blockIdx, block] of col.children.entries()) {
            const previousUid = block.uid;
            block["uid"] = shortid.generate();
            if (block.tagName === "mj-feed-block" && !firstFeedBlock) {
              firstFeedBlock = {
                sectionIndex: sectionIndex,
                columnIndex: colIdx,
                blockIndex: blockIdx,
                uid: block.uid,
              };
            }
            const blockTranslation = primaryTranslation[previousUid];
            if (!mjmlStore.mjml.langs && blockTranslation) {
              if ("content" in blockTranslation && "content" in block) {
                block.content = blockTranslation.content;
              }
              if ("attributes" in blockTranslation) {
                for (const attribute in blockTranslation.attributes) {
                  block.attributes[attribute] =
                    blockTranslation.attributes[attribute];
                }
              }
              if ("children" in blockTranslation && "children" in block) {
                for (let i = 0; i < block.children.length; i++) {
                  const child = block.children[i];
                  const childTranslation = blockTranslation.children?.[i];
                  if (
                    childTranslation &&
                    "attributes" in childTranslation &&
                    child.attributes
                  ) {
                    for (const attribute in childTranslation.attributes) {
                      // @ts-expect-error TODO: fix noImplicitAny error here
                      child.attributes[attribute] =
                        childTranslation.attributes[attribute];
                    }
                  }
                }
              }
            }
            if (mjmlStore.mjml.langs) {
              mjmlStore.addSpecialBlockTranslations({
                block: block,
                previousUid: previousUid,
                translations: premBlock.translation,
                primaryTranslation: primaryTranslation,
              });
            }
          }
        }
        props["uid"] = shortid.generate();
        // @ts-expect-error TODO: fix noImplicitAny error here
        delete props.sectionIndex;

        mjmlStore.mjml.children[0].children.splice(sectionIndex, 0, props);
        sectionIndex++;
      }

      if (data.emptyBlock) {
        mjmlStore.mjml.children[0].children.splice(sectionIndex, 1);
      }

      mjmlStore.edittedWithoutSave = true;

      if (firstFeedBlock) {
        mjmlStore.selectBlock({
          sectionIndex: firstFeedBlock.sectionIndex,
          columnIndex: firstFeedBlock.columnIndex,
          blockIndex: firstFeedBlock.blockIndex,
        });
        const feedUid = firstFeedBlock.uid;
        await nextTick();
        window.$event.emit("OPEN_FEED_PICKER", { uid: feedUid });
      }
    },
    setPremadeBlocks(premadeBlocks: IOptionsPremadeBlock) {
      // If premade blocks are disabled, set it to false
      if (!premadeBlocks) {
        this.premadeBlocks = false;
        return;
      }

      // If premade blocks are aray, set it to the array
      if (Array.isArray(premadeBlocks)) {
        this.premadeBlocks = getCorrectBlocksArray(premadeBlocks);
        return;
      }

      // If premade blocks are object, check if it has override property to check if it is Legacy premade blocks
      // @ts-expect-error
      if (!premadeBlocks.blocks) {
        const legacyBlocks = getCorrectLegacyBlocks(
          premadeBlocks as ILegacyPremadeBlocks
        );
        if (!legacyBlocks) {
          this.premadeBlocks = [];
          return;
        }
        this.premadeBlocks = this.convertLegacyPremadeBlocks(legacyBlocks);
        return;
      }

      // If premade blocks are object and are not Legady premde blocks parse "override" schema
      const {
        error: errorOver,
        data: dataOver,
        success: succesOver,
      } = PremadeBlocksOverrideSchema.safeParse(premadeBlocks);
      if (!succesOver) {
        console.error("Error while parsing premade blocks", errorOver.issues);
        const notificationsStore = useNotificationsStore();
        notificationsStore.createNotification({
          text: "Error while parsing premade blocks.",
          type: "error",
        });
        this.premadeBlocks = [];
        return;
      }

      const generalStore = useGeneralStore();
      const defaultPremadeLegacyBlocks =
        generalStore.language === "cs" ? czpremadeblocks : premadeblocks;

      // Legacy keyed shape under `blocks`.
      if (!Array.isArray(dataOver.blocks)) {
        if (dataOver.override) {
          const legacyBlocks = getCorrectLegacyBlocks(
            premadeBlocks as ILegacyPremadeBlocks
          );
          if (!legacyBlocks) {
            this.premadeBlocks = [];
            return;
          }
          this.premadeBlocks = this.convertLegacyPremadeBlocks(legacyBlocks);
          return;
        }
        const legacyBlocks = getCorrectLegacyBlocks(
          dataOver.blocks as ILegacyPremadeBlocks
        );
        this.premadeBlocks = this.convertLegacyPremadeBlocks(
          defaultPremadeLegacyBlocks,
          legacyBlocks
        );
        return;
      }

      // Array shape: custom groups and preset markers in the integrator's order.
      this.premadeBlocks = resolvePremadeBlocks({
        entries: dataOver.blocks,
        override: dataOver.override,
        defaults: defaultPremadeLegacyBlocks,
        t: (key) => i18n.global.t(key),
      });
    },
    convertLegacyPremadeBlocks(
      premadeBlocks: ILegacyPremadeBlocks,
      otherPremadeBlocks?: ILegacyPremadeBlocks | false
    ) {
      const fixedPremadeBlocks: IPremadeBlocks = [];

      if (!premadeBlocks) {
        return fixedPremadeBlocks;
      }

      for (const preset of PRESET_IDS) {
        const { legacyKey, nameKey } = PRESET_GROUPS[preset];
        const blocks = premadeBlocks[legacyKey];
        if (blocks) {
          fixedPremadeBlocks.push({ name: i18n.global.t(nameKey), blocks });
        }
      }

      // Blocks the integrator supplied under the same keys are merged into the
      // matching default group, or appended as their own group if it is absent.
      if (otherPremadeBlocks) {
        for (const preset of PRESET_IDS) {
          const { legacyKey, nameKey } = PRESET_GROUPS[preset];
          const blocks = otherPremadeBlocks[legacyKey];
          if (!blocks) continue;

          const name = i18n.global.t(nameKey);
          const existingGroup = fixedPremadeBlocks.find(
            (group) => group.name === name
          );
          if (existingGroup) {
            existingGroup.blocks.push(...blocks);
          } else {
            fixedPremadeBlocks.push({ name, blocks });
          }
        }
      }

      const { error, data, success } =
        PremadeBlocksSchema.safeParse(fixedPremadeBlocks);

      if (!success) {
        console.error("Error while parsing premade blocks", error.issues);
        const notificationsStore = useNotificationsStore();
        notificationsStore.createNotification({
          text: "Error while parsing premade blocks.",
          type: "error",
        });
        return [];
      }

      return data;
    },
  },
});

function getCorrectBlocksArray(blocks: IPremadeBlocks) {
  const { error, data, success } = PremadeBlocksSchema.safeParse(blocks);

  if (!success || !data) {
    console.error("Error while parsing premade blocks", error.issues);
    const notificationsStore = useNotificationsStore();
    notificationsStore.createNotification({
      text: "Error while parsing premade blocks.",
      type: "error",
    });
    return [];
  }
  return data;
}

function getCorrectLegacyBlocks(blocks: ILegacyPremadeBlocks) {
  const { error, data, success } = LegacyPremadeBlocksSchema.safeParse(blocks);

  if (!success || !data) {
    console.error("Error while parsing premade blocks", error.issues);
    const notificationsStore = useNotificationsStore();
    notificationsStore.createNotification({
      text: "Error while parsing premade blocks.",
      type: "error",
    });
    return false;
  }
  return data;
}

async function selectPrimaryLanguage(
  props: SelectPrimaryBlockLanguage
): Promise<string | null> {
  return new Promise((resolve) => {
    // Předáme modalu pole jazyků a callback
    window.$event.emit("selectPrimaryLanguage", {
      langs: props.langs,
      title: props.title,
      description: props.description,
      resolve, // ten zavolá modal, až bude mít výběr
    });
  });
}
